import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, History, Plus, Search, Star } from 'lucide-react';
import type { DiagnosisPick, DiagnosisPicksResponse } from '@his/shared';
import { del, get, post, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { tr } from '@/lib/i18n-core';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { EmrPanel } from './EmrPanel';

interface IcdResult {
  code: string;
  title: string;
  chapter: string | null;
}

type PickList = 'favourites' | 'recent' | 'common';

const PICKS_KEY = 'diagnosis-picks';

function PickRow({
  pick,
  added,
  favourite,
  readOnly,
  onAdd,
  onStar,
}: {
  pick: DiagnosisPick;
  added: boolean;
  favourite: boolean;
  readOnly: boolean;
  onAdd: (pick: DiagnosisPick) => void;
  onStar: (pick: DiagnosisPick, on: boolean) => void;
}) {
  return (
    <li className="flex items-center gap-1">
      <button
        type="button"
        disabled={readOnly || added}
        onClick={() => onAdd(pick)}
        className={cn(
          'group flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-2.5 py-2 text-start text-sm transition-colors focus-ring',
          added ? 'bg-success/[0.06]' : 'hover:bg-muted disabled:opacity-50',
        )}
      >
        <span className="w-14 shrink-0 font-mono text-xs font-semibold text-primary">{pick.code}</span>
        <span className="min-w-0 flex-1 leading-snug">{pick.title}</span>
        {added ? (
          <Check className="h-4 w-4 shrink-0 text-success" aria-label={tr('opd', 'dx_added')} />
        ) : (
          <Plus className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden />
        )}
      </button>
      {!readOnly && (
        <button
          type="button"
          aria-pressed={favourite}
          aria-label={favourite ? tr('opd', 'dx_unstar') : tr('opd', 'dx_star')}
          title={favourite ? tr('opd', 'dx_unstar') : tr('opd', 'dx_star')}
          onClick={() => onStar(pick, !favourite)}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors hover:bg-muted focus-ring"
        >
          <Star className={cn('h-4 w-4', favourite ? 'fill-warning text-warning' : 'text-muted-foreground/60')} aria-hidden />
        </button>
      )}
    </li>
  );
}

function useFavouriteToggle() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const setFavourites = (update: (list: DiagnosisPick[]) => DiagnosisPick[]) =>
    queryClient.setQueriesData<DiagnosisPicksResponse>({ queryKey: [PICKS_KEY] }, (old) => (old ? { ...old, favourites: update(old.favourites) } : old));
  return useMutation({
    mutationFn: ({ pick, on }: { pick: DiagnosisPick; on: boolean }) =>
      on ? post<DiagnosisPick[]>('/emr/favourite-diagnoses', { icdCode: pick.code }) : del<DiagnosisPick[]>(`/emr/favourite-diagnoses/${encodeURIComponent(pick.code)}`),
    onMutate: ({ pick, on }) => setFavourites((list) => (on ? [pick, ...list.filter((entry) => entry.code !== pick.code)] : list.filter((entry) => entry.code !== pick.code))),
    onSuccess: (list) => setFavourites(() => list),
    onError: (error) => {
      void queryClient.invalidateQueries({ queryKey: [PICKS_KEY] });
      toast.error(tr('opd', 'dx_star_failed'), describeError(error));
    },
  });
}

export function DiagnosisPicker({
  readOnly,
  chosen,
  departmentId,
  onAdd,
}: {
  readOnly: boolean;
  chosen: ReadonlySet<string>;
  departmentId: string | null | undefined;
  onAdd: (pick: DiagnosisPick) => void;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [list, setList] = useState<PickList | null>(null);
  const star = useFavouriteToggle();

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(timer);
  }, [term]);

  const picks = useQuery({
    queryKey: [PICKS_KEY, departmentId ?? ''],
    queryFn: () => get<DiagnosisPicksResponse>(`/emr/diagnosis-picks${qs({ departmentId: departmentId ?? undefined })}`),
    enabled: !readOnly && departmentId !== undefined,
    staleTime: 5 * 60_000,
  });
  const results = useQuery({
    queryKey: ['icd', debounced],
    queryFn: () => get<IcdResult[]>(`/icd/search${qs({ q: debounced, limit: 12 })}`),
    enabled: debounced.length >= 2,
  });

  const data = picks.data ?? { favourites: [], recent: [], common: [] };
  const favourites = new Set(data.favourites.map((pick) => pick.code));
  const fallback: PickList = data.favourites.length > 0 ? 'favourites' : data.recent.length > 0 ? 'recent' : 'common';
  const current = list ?? fallback;
  const searching = debounced.length >= 2;
  const rows: DiagnosisPick[] = searching ? (results.data ?? []).map((row) => ({ code: row.code, title: row.title })) : data[current];
  const onStar = (pick: DiagnosisPick, on: boolean) => star.mutate({ pick, on });
  const emptyText = searching
    ? results.isFetching
      ? null
      : tr('opd', 'no_match')
    : picks.isLoading
      ? null
      : tr('opd', `dx_empty_${current}`);

  return (
    <EmrPanel icon={Search} title={tr('opd', 'dx_find_title')} description={tr('opd', 'dx_find_desc')} bodyClassName="space-y-3 p-4">
      <div className="relative">
        <Search className="pointer-events-none absolute start-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden />
        <Input
          className="ps-8"
          placeholder={tr('opd', 'code_or_description_misspellings_are_tolerated')}
          aria-label={tr('opd', 'search_icd_10')}
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          disabled={readOnly}
        />
      </div>
      {!searching && (
        <ChoiceButtons
          label={tr('opd', 'dx_lists')}
          value={current}
          size="sm"
          options={[
            { value: 'favourites', label: tr('opd', 'dx_list_favourites') },
            { value: 'recent', label: tr('opd', 'dx_list_recent') },
            { value: 'common', label: tr('opd', 'dx_list_common') },
          ]}
          onChange={(next) => setList(next)}
        />
      )}
      {!searching && (
        <p className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
          {current === 'favourites' ? <Star className="h-3.5 w-3.5" aria-hidden /> : current === 'recent' ? <History className="h-3.5 w-3.5" aria-hidden /> : <Building2 className="h-3.5 w-3.5" aria-hidden />}
          {tr('opd', `dx_hint_${current}`)}
        </p>
      )}
      <ul className="max-h-[22rem] space-y-0.5 overflow-y-auto">
        {rows.map((pick) => (
          <PickRow key={pick.code} pick={pick} added={chosen.has(pick.code)} favourite={favourites.has(pick.code)} readOnly={readOnly} onAdd={onAdd} onStar={onStar} />
        ))}
      </ul>
      {rows.length === 0 && emptyText && <p className="rounded-lg border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">{emptyText}</p>}
    </EmrPanel>
  );
}
